import type { RootState } from '~/app/store/reducers'
import PubNub from 'pubnub'
import { Provider } from '~/entities/provider'
import { useRef } from 'react'
import { useSelector } from 'react-redux'
import { useMutation } from '~/shared/hooks/query'
import chatSlice from './chatSlice'
import type { PresenceState } from './chatSlice/slice'
import { userProvidersMeAuthenticateChatV2 } from '@fireflyhealth/api'
import invariant from 'tiny-invariant'
import { useAppDispatch } from '~/app/store/hooks'
import { logger } from '~/shared/lib/logger'

export const providerInboxChannel = (me: {
  provider_fields: Pick<NonNullable<Provider['provider_fields']>, 'tenant'>
}) => {
  return 'provider-message-inbox-' + me.provider_fields.tenant
}

export const disconnectFromPubNub = (
  pubNub: PubNub | null,
  me: { provider_fields: Pick<NonNullable<Provider['provider_fields']>, 'tenant'>; id: number },
) => {
  // Clear presence state and unsubscribe from all channels
  if (me) {
    updatePresence(pubNub, me, { typing: false, viewing: false })
  }
  if (pubNub) {
    pubNub.unsubscribeAll()
  } else {
    logger.warn('[Session] Unable to disconnect from PubNub, no client found')
  }
}

// Use short prop names to keep payload size small
// https://www.pubnub.com/docs/sdks/redux/api-reference/signals
// A type alias, not an interface: only an alias is assignable to PubNub's `Payload`.
export type PresenceSignalMessage = {
  p?: number // Patient User ID
  pe?: number // PErson ID
  v?: boolean // is viewing?
  t?: boolean // is typing?
}

const updatePresence = (
  pubNub: PubNub | null,
  me: { provider_fields: Pick<NonNullable<Provider['provider_fields']>, 'tenant'>; id: number },
  state: PresenceState,
) => {
  if (pubNub && me) {
    // https://www.pubnub.com/docs/sdks/redux/api-reference/signals
    const message: PresenceSignalMessage = {
      v: state.viewing,
      t: state.typing,
      p: state.patientId,
    }
    pubNub.signal(
      {
        channel: providerInboxChannel(me),
        message,
      },
      status => {
        if (status.error) {
          logger.error('[Chat] update presence', status)
        }
      },
    )
  }
}

export const usePubNubUtils = () => {
  const pubNub = useSelector((state: RootState) => state.chat?.pubnub)
  // Assert that provider details have been loaded
  const me = useSelector(state => state.me)
  const dispatch = useAppDispatch()
  const reAuthenticationTimeoutIdRef = useRef<NodeJS.Timeout | undefined>(undefined)

  const cancelReAuthentication = () => {
    if (reAuthenticationTimeoutIdRef.current) clearTimeout(reAuthenticationTimeoutIdRef.current)
  }

  const { mutate, mutateAsync } = useMutation(
    async () => {
      const { data } = await userProvidersMeAuthenticateChatV2.create()
      return data
    },
    {
      onSuccess: data => {
        const { id, provider_fields: providerFields } = me
        invariant(id !== null, 'Expected me.id to be not null')
        invariant(providerFields !== null, 'Expected me.provider_fields to be not null')
        try {
          dispatch(
            chatSlice.thunks.initPubnub(
              { id, provider_fields: providerFields },
              data.token,
              data.uuid,
              mutateAsync,
            ),
          )
        } catch (e) {
          // Absorbed: `new PubNub()` throws on a missing subscribe key — an environment
          // misconfiguration the session survives, minus chat presence.
          logger.error('[Chat] init PubNub', e)
        }
        // We want to fetch a new token ahead of the token's TTL
        // to silently keep the user authenticated as long as they're active
        cancelReAuthentication()
        reAuthenticationTimeoutIdRef.current = setTimeout(
          () => {
            if (me) {
              mutate()
            }
          },
          Math.floor(data.access_ttl / 2),
        )
      },
    },
    { error: 'Failed to authenticate with chat service' },
  )

  return {
    disconnectFromPubNub: () => {
      // Throws if this runs before the `me` slice has loaded.
      invariant(me.id !== null, 'Expected me.id to be not null')
      invariant(me.provider_fields !== null, 'Expected me.provider_fields to be not null')

      disconnectFromPubNub(pubNub, {
        provider_fields: me.provider_fields,
        id: me.id,
      })
      cancelReAuthentication()
    },
    updatePresence: (state: PresenceState) => {
      // Throws if this runs before the `me` slice has loaded.
      invariant(me.id !== null, 'Expected me.id to be not null')
      invariant(me.provider_fields !== null, 'Expected me.provider_fields to be not null')

      updatePresence(
        pubNub,
        {
          provider_fields: me.provider_fields,
          id: me.id,
        },
        state,
      )
    },
    authenticate: mutate,
  }
}
